import React, { useState, useEffect } from 'react';
import api from '../../../services/api';

const CompanyTab = () => {
  const [companyData, setCompanyData] = useState({
    nazwa: '', adres_ulica: '', adres_kod: '', adres_miasto: '',
    nip: '', regon: '', krs: '', telefon: '', email: '', www: '',
    kapital_zakladowy: '', prezes: '', bank_nazwa: '', bank_numer_konta: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    basic: true, legal: false, contact: false, bank: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  useEffect(() => { loadCompanyData(); }, []);

  const loadCompanyData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/company');
      const data = response.data;
      if (data.success && data.data.company) setCompanyData(data.data.company);
    } catch (error) {
      setMessage({ type: 'error', text: 'Błąd ładowania danych firmy' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setCompanyData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);
      const response = await api.post('/admin/company', companyData);
      const data = response.data;
      if (data.success) {
        setMessage({ type: 'success', text: 'Zapisano' });
        await loadCompanyData();
      } else {
        setMessage({ type: 'error', text: data.message || 'Błąd' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Błąd zapisywania' });
    } finally {
      setSaving(false);
    }
  };

  const headerStyle = (isExpanded) => ({
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '6px 10px', backgroundColor: isExpanded ? 'var(--info)' : 'var(--bg)',
    color: isExpanded ? '#fff' : 'var(--text-secondary)', border: '1px solid var(--border)',
    borderRadius: isExpanded ? '4px 4px 0 0' : '4px', cursor: 'pointer',
    fontWeight: '600', fontSize: '11px', transition: 'all 0.15s'
  });

  const contentStyle = (isExpanded) => ({
    display: isExpanded ? 'block' : 'none', padding: '8px 10px',
    backgroundColor: 'var(--card-bg)', border: '1px solid var(--border)', borderTop: 'none',
    borderRadius: '0 0 4px 4px'
  });

  const inputStyle = {
    width: '100%', padding: '4px 6px', fontSize: '11px',
    border: '1px solid var(--border)', borderRadius: '3px', outline: 'none'
  };

  const labelStyle = {
    fontSize: '11px', fontWeight: '500', marginBottom: '2px',
    display: 'block', color: 'var(--text-muted)'
  };

  if (loading) return <div style={{ padding: '1rem', textAlign: 'center', fontSize: '11px' }}>Ładowanie...</div>;

  return (
    <div style={{ padding: '8px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <span style={{ fontWeight: '600', fontSize: '12px' }}>🏢 Dane Firmy</span>
        <button onClick={handleSave} disabled={saving} style={{
          padding: '4px 10px', backgroundColor: saving ? 'var(--text-muted)' : 'var(--success)',
          color: '#fff' ,
          cursor: saving ? 'not-allowed' : 'pointer', fontSize: '11px', fontWeight: '500'
        }}>
          {saving ? 'Zapisywanie...' : '💾 Zapisz'}
        </button>
      </div>

      {message && (
        <div style={{
          padding: '4px 8px', marginBottom: '8px', fontSize: '10px',
          backgroundColor: message.type === 'success' ? 'var(--success-light)' : 'var(--danger-light)',
          color: message.type === 'success' ? 'var(--success)' : 'var(--danger)',
          borderRadius: '3px'
        }}>{message.text}</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {/* Podstawowe */}
        <div>
          <div style={headerStyle(expandedSections.basic)} onClick={() => toggleSection('basic')}>
            <span>📋 Podstawowe</span>
            <span>{expandedSections.basic ? '▼' : '▶'}</span>
          </div>
          <div style={contentStyle(expandedSections.basic)}>
            <div className="admin-form-grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Nazwa firmy *</label>
                <input type="text" value={companyData.nazwa} onChange={(e) => handleInputChange('nazwa', e.target.value)} placeholder="Nazwa firmy" style={inputStyle} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Ulica</label>
                <input type="text" value={companyData.adres_ulica} onChange={(e) => handleInputChange('adres_ulica', e.target.value)} placeholder="ul. Przykładowa 1" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Kod</label>
                <input type="text" value={companyData.adres_kod} onChange={(e) => handleInputChange('adres_kod', e.target.value)} placeholder="00-000" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Miasto</label>
                <input type="text" value={companyData.adres_miasto} onChange={(e) => handleInputChange('adres_miasto', e.target.value)} placeholder="Miasto" style={inputStyle} />
              </div>
            </div>
          </div>
        </div>

        {/* Prawne */}
        <div>
          <div style={headerStyle(expandedSections.legal)} onClick={() => toggleSection('legal')}>
            <span>⚖️ Dane prawne</span>
            <span>{expandedSections.legal ? '▼' : '▶'}</span>
          </div>
          <div style={contentStyle(expandedSections.legal)}>
            <div className="admin-form-grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
              <div>
                <label style={labelStyle}>NIP *</label>
                <input type="text" value={companyData.nip} onChange={(e) => handleInputChange('nip', e.target.value)} placeholder="NIP" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>REGON</label>
                <input type="text" value={companyData.regon} onChange={(e) => handleInputChange('regon', e.target.value)} placeholder="REGON" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>KRS</label>
                <input type="text" value={companyData.krs} onChange={(e) => handleInputChange('krs', e.target.value)} placeholder="KRS" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Kapitał</label>
                <input type="text" value={companyData.kapital_zakladowy} onChange={(e) => handleInputChange('kapital_zakladowy', e.target.value)} placeholder="50.000 zł" style={inputStyle} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Prezes/Zarząd</label>
                <input type="text" value={companyData.prezes} onChange={(e) => handleInputChange('prezes', e.target.value)} placeholder="Jan Kowalski" style={inputStyle} />
              </div>
            </div>
          </div>
        </div>

        {/* Kontakt */}
        <div>
          <div style={headerStyle(expandedSections.contact)} onClick={() => toggleSection('contact')}>
            <span>📞 Kontakt</span>
            <span>{expandedSections.contact ? '▼' : '▶'}</span>
          </div>
          <div style={contentStyle(expandedSections.contact)}>
            <div className="admin-form-grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
              <div>
                <label style={labelStyle}>Telefon</label>
                <input type="text" value={companyData.telefon} onChange={(e) => handleInputChange('telefon', e.target.value)} placeholder="+48..." style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Email</label>
                <input type="email" value={companyData.email} onChange={(e) => handleInputChange('email', e.target.value)} placeholder="email@firma.pl" style={inputStyle} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>WWW</label>
                <input type="url" value={companyData.www} onChange={(e) => handleInputChange('www', e.target.value)} placeholder="www.firma.pl" style={inputStyle} />
              </div>
            </div>
          </div>
        </div>

        {/* Bank */}
        <div>
          <div style={headerStyle(expandedSections.bank)} onClick={() => toggleSection('bank')}>
            <span>🏦 Bank</span>
            <span>{expandedSections.bank ? '▼' : '▶'}</span>
          </div>
          <div style={contentStyle(expandedSections.bank)}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div>
                <label style={labelStyle}>Nazwa banku</label>
                <input type="text" value={companyData.bank_nazwa} onChange={(e) => handleInputChange('bank_nazwa', e.target.value)} placeholder="Bank S.A." style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Numer konta</label>
                <input type="text" value={companyData.bank_numer_konta} onChange={(e) => handleInputChange('bank_numer_konta', e.target.value)} placeholder="PL 12 3456..." style={inputStyle} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: '8px', padding: '6px', backgroundColor: 'var(--bg)', borderRadius: '3px', fontSize: '9px', color: 'var(--text-muted)' }}>
        ℹ️ Dane używane w fakturach. Pola z * są wymagane.
      </div>
    </div>
  );
};

export default CompanyTab;
